import { APPROXIMATE_NOTE, EMPTY, asOfDay, dateRange, descriptiveLegend, eventName, fixed, pctText, pyRound, safeCopy, shortDate, toNum, topPct } from "../../../../utils/perfFormat";
import { PLAY_GROUP_LABELS, PLAY_GROUP_ORDER } from "../../../../utils/playGroups";
import { CHART, ORANGE_ALPHA, ROLE, archetypeBase, archetypeColor } from "../../../ForBrands/ui/charts/chartTokens";
import { LEAGUE_STATS_PROVIDER } from "../../PlayerProfile/profileModel";

// The athlete profile's Performance section (Jayson's bb_performance cards,
// spec B3-B6) as view models: pure functions from the endpoint's envelope
// (business/athletes/about/.../performance/) and the profile model to what
// each card prints. No React.
//
// - The ledes are written here, in the reader's voice: the second person on
//   the athlete's own profile ("Where your points come from"), the first
//   name for everyone else ("Where Riley's points come from"), never a
//   pronoun, so they read the same for every athlete.
// - Payload text (insights, archetype descriptions, the method) has been
//   rewritten by the server already (no em dash, no data provider, the
//   athlete's pronouns); safeCopy is only the safety net.
// - Percentiles read "Top N%" with the payload's rounding (perfFormat), a
//   role stat's (descriptive) the plain "88th pct", a missing value is EMPTY
//   (the en dash) and a missing percentile is "Not ranked": nothing is ever
//   made up.

const text = (v) => (v === null || v === undefined ? "" : String(v).trim());
const list = (v) => (Array.isArray(v) ? v.filter((item) => item && typeof item === "object") : []);
const capital = (s) => (s ? `${s.charAt(0).toUpperCase()}${s.slice(1)}` : s);
const count = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const whole = (v) => {
  const n = toNum(v);
  return n === null ? null : Math.round(n);
};
const COUNT_WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];
const countWord = (n) => COUNT_WORDS[n] || String(n);

// The payload's insights as InsightList takes them, in payload order.
const insightsOf = (items) => list(items).filter((i) => text(i.title) || text(i.detail));

// The profile reads the new cards only when the about payload says so
// (performanceV2.available, profileModel's `performanceV2`).
export const hasPerformanceV2 = (model) => model?.performanceV2 === true;

// ------------------------------------------------------------------ voice
// Who the copy is about. `own` tags the athlete's own archetype and band
// (" · You", " · Riley").
export const voiceFor = ({ selfView = false, firstName = "" } = {}) => {
  const first = text(firstName);
  if (selfView) return { self: true, first, whose: "Your", whoseLower: "your", them: "you", own: "You", name: "You" };
  return {
    self: false,
    first,
    whose: first ? `${first}'s` : "This athlete's",
    whoseLower: first ? `${first}'s` : "this athlete's",
    them: first || "this athlete",
    own: first || "Athlete",
    name: first || "Athlete",
  };
};

// ------------------------------------------------------------------- peer
// A competition as the cards name it: no "(Boys)" / "(Girls)".
export const plainCompetition = (name) =>
  text(name)
    .replace(/\s*\((?:boys|girls|men|women)\)\s*/gi, " ")
    .replace(/\s{2,}/g, " ")
    .trim();

// A season of games the athlete's team entered itself (the game sheet,
// spec D8): no league feed behind it, so no peer group ever ranks it. The
// server marks the envelope, its season option or its payload `entered`.
export const enteredOf = (envelope) =>
  Boolean(envelope && typeof envelope === "object") &&
  (envelope.entered === true || envelope.season?.entered === true || envelope.payload?.entered === true);

// The group the percentiles rank against: { label, season, name ("Test
// League 16U 2025-26"), n, nText ("151 rotation players"), ranked,
// approximate, building, camp (the season shown is a camp: no peer group
// ever ranks one), entered (games the team entered: none either) }.
export const peerOf = (envelope) => {
  const p = (envelope && typeof envelope === "object" && (envelope.peer || envelope.payload?.peer)) || {};
  const label = plainCompetition(p.label);
  const season = text(p.season);
  const name = label && season && !label.includes(season) ? `${label} ${season}` : label || season;
  const n = toNum(p.n ?? p.n_rotation);
  const size = n !== null && n > 0 ? Math.round(n) : null;
  return {
    label,
    season,
    name,
    n: size,
    nText: size ? `${size.toLocaleString("en-US")} rotation players` : "",
    ranked: p.ranked === true || (p.ranked == null && size !== null),
    approximate: p.approximate === true,
    building: p.building === true,
    camp: envelope?.season?.camp === true,
    entered: enteredOf(envelope),
  };
};

// Who a ranked season is ranked against: "Test League 16U 2025-26", else
// the group's size, else the plain words (a group the payload does not
// name still ranks: never "Not ranked" beside real ranks).
const groupName = (peer) => peer.name || peer.nText || "the peer group";

// "percentile vs Test League 16U 2025-26, 151 rotation players"
const versus = (peer) => [`percentile vs ${groupName(peer)}`, peer.name && peer.nText].filter(Boolean).join(", ");

// A name after our own "the": a competition named with its article ("The
// Border League Varsity") reads "the Border League Varsity", never "the The".
const theName = (name) => `the ${text(name).replace(/^the\s+/i, "")}`;

// Why nothing is ranked (B11: a peer group too small or still being built;
// a camp never has one, nor do games the team entered).
export const unrankedNote = (peer) => {
  if (peer?.camp) return "Not ranked: camps have no peer group.";
  if (peer?.entered) return "Not ranked: games entered by the team have no peer group.";
  if (peer?.building) return peer.label ? `Ranks appear once ${theName(peer.label)} peer group is ready.` : "Ranks appear once the peer group is ready.";
  return `Not ranked: there is no peer group for ${peer?.label || "this league"} yet.`;
};

// A lede that gains a sentence ends the one before it with a period.
const sentence = (s) => (s.endsWith(".") ? s : `${s}.`);

// What the "≈" on the chips means (perfFormat.APPROXIMATE_NOTE, the coach
// team page's snapshot lede says it too), after a ranked lede whose ranks
// come from an approximate peer norm (the lede then ends with a period).
const withApproximate = (lede, peer) => (peer.ranked && peer.approximate ? `${sentence(lede)} ${APPROXIMATE_NOTE}` : lede);

// ------------------------------------------------------------ rank season
// The season the ranked cards come from when the default season can never
// be ranked (spec D4 d: a camp, a short event, a league too small): the
// payload's rank_season { key, label }, with its own peer description when
// the server sends one (the envelope's `peer` stays the default season's).
// null when the cards read the default season itself. Game by Game always
// stays on the default season. A rank season whose peer says `building`
// (its group is being built) ranks nothing yet: the archetype's shell
// shows and the section asks again.
export const rankSeasonOf = (envelope) => {
  const env = envelope && typeof envelope === "object" ? envelope : {};
  const payload = env.payload && typeof env.payload === "object" ? env.payload : {};
  const r = [payload.rank_season, payload.rankSeason, env.rank_season, env.rankSeason].find((x) => x && typeof x === "object");
  const label = text(r?.label);
  if (!label) return null;
  const key = text(r.key);
  if (key && key === text(env.season?.key)) return null;
  return { key, label, peer: r.peer && typeof r.peer === "object" ? r.peer : null };
};

// The group of the rank season: its own peer description when the payload
// names one, the envelope's when that ranks, else the rank season by its
// label, ranked (a rank season is named once a peer group ranks it).
const rankPeerOf = (basis, peer) => {
  if (basis.peer) return { ...peerOf({ peer: basis.peer }), camp: false, entered: false };
  if (peer.ranked) return peer;
  return { ...peer, label: basis.label, season: "", name: basis.label, n: null, nText: "", ranked: true, building: false, camp: false, entered: false };
};

// "... Based on 2025-26 Test League 16U, the latest season with a peer
// group." after the archetype's ranked lede, when a rank season stands in.
const withBasis = (lede, basis) => (basis ? `${sentence(lede)} Based on ${basis.label}, the latest season with a peer group.` : lede);

// ----------------------------------------------------- archetype's place
// Why the profile's radar holds the Player Archetype's place (spec D4 c):
// no peer group can come for the season (a camp, a short event, a league
// too small), or one ranks it but its box scores lack the five metrics.
export const RADAR_FALLBACK_NOTE = "The Player Archetype needs a league season with a peer group; camps and short events have none.";
export const RADAR_NO_METRICS_NOTE = "The Player Archetype needs the five archetype metrics, which this season's box scores do not have.";
// The same, for a rank season (D4 d) that a peer group ranks: named, since
// the season on screen is another one.
export const radarNoMetricsNote = (label) =>
  text(label) ? `The Player Archetype needs the five archetype metrics, which ${theName(label)} box scores do not have.` : RADAR_NO_METRICS_NOTE;
// Games the team entered (the default season, spec D8): the archetype and
// the comps are left out, and the section says why in their place.
export const ENTERED_ARCHETYPE_NOTE =
  "The Player Archetype and Historical Comps need a league season with a peer group; games entered by the team have none.";
// Game by Game on a season of games the team entered.
export const ENTERED_GAMES_NOTE = "These games were entered by the team.";

// "Key stats ..., percentile vs Test League 16U 2025-26, 151 rotation
// players", or the head and why nothing ranks.
const rankLede = (head, peer) => (peer.ranked ? withApproximate(capital(`${head}, ${versus(peer)}`), peer) : `${capital(head)}. ${unrankedNote(peer)}`);

// -------------------------------------------------------------- archetype
const metricView = (m) => {
  const decimals = Number.isInteger(m.decimals) && m.decimals >= 0 && m.decimals <= 3 ? m.decimals : 1;
  const p = toNum(m.percentile);
  const code = text(m.label) || text(m.metric).toUpperCase();
  const title = text(m.title) || capital(text(m.name)) || code;
  const raw = fixed(m.raw, decimals);
  return {
    key: text(m.metric) || code,
    code,
    title,
    question: safeCopy(m.question),
    raw,
    percentile: p,
    top: p === null ? "Not ranked" : topPct(p),
    hot: p !== null && p >= 90,
    median: fixed(m.median, decimals),
    // A role stat behind a metric (usage) reads its plain percentile, as its
    // Skill Profile chip does (B5).
    drivers: list(m.drivers).map((d, i) => {
      const key = text(d.key);
      let top = "";
      if (toNum(d.percentile) !== null) top = DESCRIPTIVE_SKILLS.has(key) ? pctText(d.percentile) : topPct(d.percentile);
      return { key: key || `${text(d.label)}-${i}`, label: text(d.label), display: text(d.display) || EMPTY, top };
    }),
  };
};

// What the Impact rating is (the data's competition-adjusted overall
// rating, renamed "Impact rating", B6), in one line for the card and one
// sentence with its scale for the archetype pop-up.
export const IMPACT_NOTE = "Overall production, adjusted for the level of competition";
export const impactMeaning = (impact) =>
  `One overall rating of a player's production, adjusted for the level of competition. Higher is better${
    impact?.median !== null && impact?.median !== undefined ? `; the division median is ${fixed(impact.median, 2)}` : ""
  }.`;

// Impact: the value, its percentile, the division median and the rank, the
// rank only when it is a real place in the group (an estimate from the
// percentile says "About").
export const impactView = (impact) => {
  if (!impact || typeof impact !== "object") return null;
  const value = toNum(impact.value);
  const percentile = toNum(impact.percentile);
  if (value === null && percentile === null) return null;
  const rank = toNum(impact.rank);
  const of = toNum(impact.of);
  const valid = rank !== null && of !== null && Number.isInteger(rank) && rank >= 1 && rank <= of;
  return {
    value: value === null ? EMPTY : value.toFixed(2),
    percentile,
    median: toNum(impact.median),
    rank: valid ? `${impact.rank_estimated ? "About " : ""}#${rank} of ${Math.round(of).toLocaleString("en-US")}` : null,
  };
};

// Player Archetype (A1): null without an archetype label (the card's shell
// shows while its peer group is being built, else the profile's older
// radar stays in its place, B3 and D4 c). `basis` (rankSeasonOf) is the
// season it comes from when the default one can never be ranked.
export const archetypeView = (payload, voice, peer, basis = null) => {
  const a = payload?.archetype;
  const label = text(a?.archetype);
  if (!a || typeof a !== "object" || !label) return null;
  const base = text(a.base_archetype) || archetypeBase(label);
  const metrics = list(a.metrics).map(metricView);
  const axes = metrics.map((m) => ({
    key: m.key,
    label: m.code,
    value: m.percentile,
    sublabel: m.percentile === null ? EMPTY : m.top,
    ariaLabel: `${m.title}: ${m.raw}, ${m.percentile === null ? "not ranked" : m.top}`,
  }));
  const archetypes = list(a.archetypes)
    .map((x) => ({ name: text(x.archetype), color: archetypeColor(x.archetype, x.color), description: safeCopy(x.description) }))
    .filter((x) => x.name)
    .map((x) => ({ ...x, own: x.name === base }));
  const summary = metrics.map((m) => `${m.code} ${m.percentile === null ? "not ranked" : m.top}`).join(", ");
  return {
    label,
    base,
    color: archetypeColor(label, a.color),
    description: safeCopy(a.description),
    method: safeCopy(a.method),
    metrics,
    axes,
    radarLabel: `Archetype radar, ${peer.ranked ? `percentile vs ${groupName(peer)}` : "not ranked"}: ${summary}`,
    lede: peer.ranked
      ? withApproximate(
        withBasis(`Percentile on ${countWord(metrics.length)} archetype metrics vs ${groupName(peer)}${peer.name && peer.nText ? `, ${peer.nText}` : ""}`, basis),
        peer,
      )
      : `${capital(countWord(metrics.length))} archetype metrics. ${unrankedNote(peer)}`,
    impact: impactView(payload?.impact),
    insights: [...insightsOf(a.insights), ...insightsOf(payload?.impact?.insights)],
    archetypes,
    archetypesTitle: `The ${countWord(archetypes.length)} archetypes`,
    own: voice.own,
  };
};

// The Player Archetype card's shell while its peer group is being built
// (spec D4 c): the unranked note ("Ranks appear once the Test League 16U
// peer group is ready.") and the archetype metrics as plain values, which
// the payload carries before any rank; the full card replaces it when the
// group is ready (useAthletePerformance asks again on its own).
export const archetypeShellView = (payload, voice, peer) => {
  const metrics = list(payload?.archetype?.metrics)
    .map(metricView)
    .filter((m) => m.code);
  return {
    lede: metrics.length ? `${capital(countWord(metrics.length))} archetype metrics. ${unrankedNote(peer)}` : unrankedNote(peer),
    metrics: metrics.map((m) => ({ key: m.key, code: m.code, title: m.title, raw: m.raw })),
  };
};

// ----------------------------------------------------------------- skills
// Metrics that describe a role or a shot choice rather than a strength
// (B5): their percentile shows in the passive chip.
export const DESCRIPTIVE_SKILLS = new Set(["usage", "three_rate", "two_rate"]);

// The mock's small-group fold: a group of one or two metrics joins the one
// before it when the two hold three metrics or fewer ("Rebounding & ball
// security").
export const mergeSkillGroups = (groups) => {
  const out = [];
  list(groups).forEach((g) => {
    const metrics = list(g.metrics);
    if (!metrics.length) return;
    const name = text(g.group) || "Other";
    const last = out[out.length - 1];
    if (last && last.metrics.length + metrics.length <= 3 && metrics.length < 3) {
      last.name = `${last.name} & ${name.toLowerCase()}`;
      last.metrics = [...last.metrics, ...metrics];
    } else {
      out.push({ name, metrics });
    }
  });
  return out;
};

export const skillsView = (payload, voice, peer) => {
  const s = payload?.skills;
  const groups = mergeSkillGroups(s?.groups).map((g) => ({
    name: g.name,
    metrics: g.metrics.map((m, i) => {
      const key = text(m.key) || `${text(m.label)}-${i}`;
      return {
        key,
        label: text(m.label),
        value: text(m.display) || EMPTY,
        percentile: toNum(m.percentile),
        kind: DESCRIPTIVE_SKILLS.has(text(m.key)) ? "descriptive" : "rank",
        median: toNum(m.median),
        unit: text(m.unit),
        lowerIsBetter: m.lower_is_better === true,
      };
    }),
  }));
  if (!groups.length) return null;
  // A ranked role stat's chip is passive grey, like the median's, and reads
  // "88th pct": the legend says what it means (perfFormat.descriptiveLegend),
  // so it never reads as a quality rank or a median.
  const described = groups
    .flatMap((g) => g.metrics)
    .filter((m) => m.kind === "descriptive" && m.percentile !== null && m.label)
    .map((m) => m.label);
  return {
    lede: rankLede("key stats per 40 minutes and efficiency", peer),
    groups,
    rankLegend: peer.ranked ? `Rank vs ${groupName(peer)}` : null,
    descriptiveLegend: descriptiveLegend(described),
    // The legend names the median chip only when one shows.
    hasMedians: groups.some((g) => g.metrics.some((m) => m.median !== null)),
    insights: insightsOf(s?.insights),
  };
};

// ------------------------------------------------------------ scoring mix
// `basis` (rankSeasonOf) names the season the totals come from when the
// default season can never be ranked.
export const scoringView = (payload, voice, peer, basis = null) => {
  const m = payload?.scoring_mix;
  const mix = list(m?.points_mix);
  const rates = list(m?.attempt_rates);
  if (!mix.length && !rates.length) return null;
  const segments = mix.map((x, i) => ({ key: text(x.key) || `mix-${i}`, label: text(x.label), share: toNum(x.share) }));
  const medians = mix.filter((x) => toNum(x.peer_median) !== null);
  const tiles = rates.map((e, i) => ({
    key: text(e.key) || `rate-${i}`,
    label: text(e.label),
    value: toNum(e.value) === null ? EMPTY : `${pyRound(e.value)}%`,
    percentile: toNum(e.percentile),
    median: toNum(e.median),
  }));
  const shotsWord = voice.self ? "you take" : `${voice.them} takes`;
  return {
    lede: `Where ${voice.whoseLower} points come from, ${basis ? `${basis.label} totals` : "season totals"}`,
    segments,
    ariaLabel: `Points mix: ${segments.map((x) => `${x.share === null ? EMPTY : `${pyRound(x.share)}%`} ${x.label}`).join(", ")}`,
    caption: medians.length ? `Division median: ${medians.map((x) => `${pyRound(x.peer_median)}% ${text(x.label)}`).join(" · ")}` : null,
    tiles,
    // Every attempt rate is descriptive ("88th pct"): the legend says what
    // its percentile is against.
    rankLegend: peer.ranked ? `Percentile vs ${groupName(peer)}` : null,
    // The legend names the median chip only when one shows.
    hasMedians: tiles.some((t) => t.median !== null),
    note: peer.ranked
      ? `Attempt rates are shots of each type per 100 field-goal attempts; their percentile is how often ${shotsWord} that shot, not how well.`
      : "Attempt rates are shots of each type per 100 field-goal attempts.",
    insights: insightsOf(m?.insights),
  };
};

// ------------------------------------------------------------ game by game
export const STAT_CODES = { points: "PTS", assists: "AST", rebounds: "REB", steals: "STL", blocks: "BLK", minutes: "MIN", ts_pct: "TS%" };
const STAT_WORDS = { points: "points", assists: "assists", rebounds: "rebounds", steals: "steals", blocks: "blocks", minutes: "minutes", ts_pct: "true shooting" };
const DEFAULT_STATS = [
  { key: "points", label: "Points" },
  { key: "assists", label: "Assists" },
  { key: "rebounds", label: "Rebounds" },
  { key: "steals", label: "Steals" },
  { key: "blocks", label: "Blocks" },
  { key: "minutes", label: "Minutes" },
  { key: "ts_pct", label: "True shooting %" },
];
const RESULT_WORDS = { W: "win", L: "loss", T: "tie" };

// A stat as the chart and the box score print it: true shooting with one
// decimal and "%", minutes whole, counts as they are (one decimal for an
// average).
export const fmtStat = (key, v) => {
  const n = toNum(v);
  if (n === null) return EMPTY;
  if (key === "ts_pct") return `${n.toFixed(1)}%`;
  if (key === "minutes") return String(Math.round(n));
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

const statPhrase = (key, v) => {
  const value = fmtStat(key, v);
  const word = STAT_WORDS[key] || key;
  return value === EMPTY ? `no ${word}` : `${value} ${word}`;
};

// A season's kind of play (spec D6, D8), as the server names each season
// option (`group`): school, grassroots (club circuits and AAU), college,
// pro, camp or other events, with the words the selector and the lede's
// badge print: utils/playGroups, the box score switch's words too.
export const GROUP_LABELS = PLAY_GROUP_LABELS;
// The selector's group order: the box score switch's (School | Grassroots,
// College / Pro, D7), then the kinds no peer group ranks.
const GROUP_ORDER = PLAY_GROUP_ORDER;

// A season's group code: the server's when it is one of GROUP_LABELS, a
// camp when it is tagged so (an answer from before groups), else "".
export const groupOf = (s) => {
  const code = text(s?.group).toLowerCase();
  if (Object.prototype.hasOwnProperty.call(GROUP_LABELS, code)) return code;
  return s?.camp === true ? "camp" : "";
};

// The season selector (B4, B15): every season the server lists, newest
// first, a camp tagged "Camp" (the label it gives), each with its group
// (D8) and whether the team entered its games. A season on file with no
// games is left out, as on the mock (picking it could only say there is
// nothing to show); the season on screen is always one of them.
const optionOf = (s) => {
  const games = whole(s.games);
  const tag = s.camp === true ? text(s.campLabel) || "Camp" : "";
  const label = text(s.label) || plainCompetition(s.competition) || "Season";
  const group = groupOf(s);
  return {
    key: text(s.key),
    label,
    camp: s.camp === true,
    tag,
    games,
    group,
    groupLabel: GROUP_LABELS[group] || "",
    entered: s.entered === true,
    text: [label, games !== null && count(games, "game"), tag].filter(Boolean).join(" · "),
  };
};

export const seasonOptions = (envelope) => {
  const shownKey = text(envelope?.season?.key);
  const options = list(envelope?.seasons)
    .filter((s) => text(s.key))
    .map(optionOf)
    .filter((o) => o.games !== 0 || o.key === shownKey);
  const shown = envelope?.season;
  if (shown && typeof shown === "object" && text(shown.key) && !options.some((o) => o.key === text(shown.key))) {
    options.unshift(optionOf(shown));
  }
  return options;
};

// The selector's groups (D8): one per group the options hold, always in
// one order (School, Grassroots, College, Pro, Camp, Other events), each
// group's seasons newest first as the server lists them; options with no
// group close the list without a heading. null with fewer than two
// groups: one plain list.
export const seasonGroups = (options = []) => {
  const all = list(options);
  const codes = GROUP_ORDER.filter((code) => all.some((o) => o.group === code));
  if (codes.length < 2) return null;
  const groups = codes.map((code) => ({ key: code, label: GROUP_LABELS[code], options: all.filter((o) => o.group === code) }));
  const loose = all.filter((o) => !o.group);
  if (loose.length) groups.push({ key: "", label: "", options: loose });
  return groups;
};

const pairOf = (stats, made, attempted) => {
  const m = whole(stats[made]);
  const a = whole(stats[attempted]);
  return m === null || a === null ? null : `${m}-${a}`;
};

// "26 min · FG 3-11 · 3P 1-7 · FT 1-6 · 2 blk · 2 TO · 33.0% TS"
export const boxLine = (stats = {}) => {
  const min = whole(stats.minutes);
  const fg = pairOf(stats, "field_goals_made", "field_goals_attempted");
  const three = pairOf(stats, "three_pointers_made", "three_pointers_attempted");
  const ft = pairOf(stats, "free_throws_made", "free_throws_attempted");
  const blk = whole(stats.blocks);
  const to = whole(stats.turnovers);
  const ts = toNum(stats.ts_pct);
  return [
    min !== null && `${min} min`,
    fg && `FG ${fg}`,
    three && `3P ${three}`,
    ft && `FT ${ft}`,
    blk !== null && `${blk} blk`,
    to !== null && `${to} TO`,
    ts !== null && `${ts.toFixed(1)}% TS`,
  ]
    .filter(Boolean)
    .join(" · ");
};

// The season's games, in date order, with what the chart, its detail card
// and the box score print. A game the athlete missed is "did not play"
// (DNP), never a 0; a game with no final score has no result.
export const gameRows = (log, { camp = false, campLabel = "Camp" } = {}) =>
  list(log?.games).map((g, i) => {
    const result = RESULT_WORDS[g.result] ? g.result : null;
    const us = whole(g.team_score);
    const them = whole(g.opponent_score);
    const score = us === null || them === null ? null : `${us}-${them}`;
    const rank = whole(g.opponent_rpi_rank);
    const of = whole(g.opponent_rpi_of);
    const isCamp = camp || g.camp === true;
    return {
      key: `${text(g.date) || "game"}-${i}`,
      date: text(g.date),
      day: shortDate(g.date),
      event: text(g.event),
      eventShort: eventName(g.event),
      opponent: text(g.opponent),
      result,
      score,
      resultText: result ? [result, score].filter(Boolean).join(" ") : EMPTY,
      played: g.played !== false,
      stats: g.stats && typeof g.stats === "object" ? g.stats : {},
      rolling: g.rolling && typeof g.rolling === "object" ? g.rolling : {},
      rpi: rank !== null && of !== null ? `#${rank} of ${of}` : null,
      camp: isCamp,
      campLabel: isCamp ? campLabel : null,
    };
  });

// "Apr 3 vs Test Club 06, win 85-77: 9 points"
export const gameAria = (row, key) => {
  const head = [row.day, row.opponent && `vs ${row.opponent}`].filter(Boolean).join(" ");
  const outcome = row.result ? [RESULT_WORDS[row.result], row.score].filter(Boolean).join(" ") : null;
  const what = row.played ? statPhrase(key, row.stats[key]) : "did not play";
  return `${[head, outcome, row.camp && (row.campLabel || "Camp").toLowerCase()].filter(Boolean).join(", ")}: ${what}`;
};

// One stat's bars for GameBars.
export const barsFor = (rows, key) =>
  rows.map((r) => ({
    key: r.key,
    value: r.played ? toNum(r.stats[key]) : null,
    label: r.day,
    result: r.result,
    event: r.event,
    camp: r.camp,
    dnp: !r.played,
    rolling: toNum(r.rolling[key]),
    ariaLabel: gameAria(r, key),
  }));

// A camp label as a game: "Camp" -> "camp game", "All-star game" ->
// "all-star game" (a label may already say "game", profileModel's
// campNoun).
const campNoun = (label) => `${text(label).toLowerCase().replace(/\s*\bgames?$/, "") || "camp"} game`;

// The chart legend for a stat (B18 colour roles): what each bar colour
// means, the same words on every stat (D9: "Team win", "Team loss"; the
// pills and the chart already name the stat and whose it is). `unscored`
// adds the hollow grey bar of a game with no final score (GameBars' "none"
// tone); `scored` false leaves out the win and loss colours no bar wears
// (a season of games the team entered with no final scores, D8). A camp
// game ("Camp game") is hollow orange on desktop and bm-orange/40 on
// phones, as GameBars draws it (`phone`). `voice` and `key` stay in the
// signature for the card's call.
export const gameLegend = (voice, key, window, { camp = false, campLabel = "Camp", unscored = false, scored = true, phone = false } = {}) => {
  const campMark = phone ? { swatch: "dot", color: ORANGE_ALPHA[40] } : { swatch: "ring", color: CHART.orange };
  const items = camp
    ? [{ key: "camp", ...campMark, label: capital(campNoun(campLabel)) }]
    : [
      scored && { key: "win", swatch: "dot", color: ROLE.win, label: "Team win" },
      scored && { key: "loss", swatch: "dot", color: ROLE.loss, label: "Team loss" },
      unscored && { key: "none", swatch: "ring", color: CHART.tertiary, label: "No final score" },
    ].filter(Boolean);
  return [
    ...items,
    { key: "rolling", swatch: "line", color: ROLE.trend, label: `Rolling ${window}-game avg` },
    { key: "season", swatch: "dashed", color: ROLE.average, label: "Season avg" },
  ];
};

// The competition lines the profile shows for the season's academic year
// (Kobi's 10/2 work: "Test League 16U, Test Club 04, 15 games").
const competitionLinesFor = (model, academic) => {
  const seasons = model?.currentSeason?.seasons;
  if (!academic || !Array.isArray(seasons)) return [];
  const season = seasons.find((s) => text(s?.label) === academic);
  return Array.isArray(season?.competitions) ? season.competitions.filter((line) => text(line)) : [];
};

export const gameLogView = (envelope, voice, model) => {
  const payload = envelope?.payload || {};
  const log = payload.game_log && typeof payload.game_log === "object" ? payload.game_log : {};
  const season = envelope?.season && typeof envelope.season === "object" ? envelope.season : {};
  const camp = season.camp === true;
  const campLabel = text(season.campLabel) || "Camp";
  const rows = gameRows(log, { camp, campLabel });
  const stats = (list(log.stats).length ? list(log.stats) : DEFAULT_STATS)
    .filter((s) => text(s.key))
    .map((s) => ({ key: text(s.key), label: text(s.label) || STAT_CODES[text(s.key)] || text(s.key) }));
  const window = whole(log.rolling_window) || 5;
  const played = whole(log.games_played);
  const record = text(log.team_record?.label);
  const label = text(season.label) || [text(payload.player?.season), plainCompetition(payload.player?.competition)].filter(Boolean).join(" ");
  const program = text(season.program) || text(payload.player?.program);
  const detail = [program, played !== null && `${count(played, "game")}${record ? ` (team ${record})` : ""}`].filter(Boolean).join(", ");
  const head = `${voice.whose} ${label ? `${label} ` : ""}season, game by game`;
  const options = seasonOptions(envelope);
  // The season's group: its own option's, else its entry in the list.
  const listed = options.find((o) => o.key === text(season.key));
  const group = groupOf(season) || listed?.group || "";
  const entered = enteredOf(envelope) || listed?.entered === true;
  return {
    rows,
    stats,
    window,
    seasonAvg: log.season_avg && typeof log.season_avg === "object" ? log.season_avg : {},
    lede: detail ? `${head}. ${detail}.` : `${head}.`,
    // The lede's passive badge: the camp's label, else the season's group.
    badge: camp ? campLabel : GROUP_LABELS[group] || null,
    group,
    competitions: competitionLinesFor(model, text(season.season)),
    camp,
    campLabel,
    campNote: camp ? `${capital(campNoun(campLabel))}s show here but never count toward the CacheScore™.` : null,
    entered,
    enteredNote: entered ? ENTERED_GAMES_NOTE : null,
    options,
    groups: seasonGroups(options),
    seasonLabel: label,
    hasRpi: rows.some((r) => r.rpi),
    range: rows.length ? dateRange(rows[0].date, rows[rows.length - 1].date) : null,
    insights: insightsOf(log.insights),
  };
};

// ------------------------------------------------------------ consistency
export const consistencyView = (payload, voice) => {
  const c = payload?.consistency;
  const played = whole(payload?.game_log?.games_played);
  const rows = list(c?.stats)
    .filter((s) => toNum(s.min) !== null && toNum(s.max) !== null)
    .map((s, i) => ({
      key: text(s.key) || `stat-${i}`,
      label: text(s.label),
      min: toNum(s.min),
      p25: toNum(s.p25),
      p75: toNum(s.p75),
      max: toNum(s.max),
      mean: toNum(s.mean),
      typicalNote: text(s.typical_note) || undefined,
    }));
  const thresholds = list(c?.thresholds)
    .filter((t) => text(t.label))
    .map((t) => {
      const games = whole(t.games) ?? 0;
      return { key: text(t.label), label: `${text(t.label)} games`, count: played !== null ? `${games} of ${played}` : String(games) };
    });
  const insights = insightsOf(c?.insights);
  if (!rows.length && !thresholds.length && !insights.length) return null;
  return { lede: `Game-to-game range of ${voice.whoseLower} main stats`, rows, thresholds, insights };
};

export const CONSISTENCY_LEGEND = [
  { key: "range", swatch: "bar", color: ORANGE_ALPHA[20], label: "Low to high game" },
  { key: "typical", swatch: "bar", color: CHART.orange, label: "Typical range (middle 50% of games)" },
  { key: "mean", swatch: "dot", color: CHART.ink, label: "Per-game average" },
];

// ------------------------------------------------------- historical comps
const COMP_STATS = [
  ["ppg", "PPG"],
  ["apg", "APG"],
  ["spg", "SPG"],
];

// Historical Comps (A6): null when the payload has no comparison pool (the
// card hides, B3). Past players are named (owner approved, B10).
export const compsView = (payload, voice) => {
  const c = payload?.comps;
  const pool = c?.pool;
  if (!pool || typeof pool !== "object") return null;
  const division = text(pool.division);
  const competition = plainCompetition(pool.competition);
  const seasons = (Array.isArray(pool.seasons) ? pool.seasons : []).map(text).filter(Boolean);
  const n = whole(pool.n);
  const from = [competition, seasons.join(" & ")].filter(Boolean).join(" ");
  const lede = `Closest ${division ? `${division} ` : ""}profiles${from ? ` from ${from}` : ""}${n ? ` (${n.toLocaleString("en-US")} rotation players)` : ""}, and where they went next`;
  const comps = list(c.comps)
    .filter((x) => text(x.name))
    .map((x, i) => {
      const stats = x.stats && typeof x.stats === "object" ? x.stats : {};
      const ts = toNum(stats.ts);
      const programs = (Array.isArray(x.outcome?.programs) ? x.outcome.programs : []).map(text).filter(Boolean);
      const level = text(x.outcome?.level);
      return {
        key: `${text(x.name)}-${i}`,
        name: text(x.name),
        similarity: toNum(x.similarity) === null ? null : `${pyRound(x.similarity)}% similar`,
        meta: [text(x.program), [text(x.season), division].filter(Boolean).join(" ")].filter(Boolean).join(" · "),
        archetype: text(x.archetype),
        color: archetypeColor(x.archetype),
        statLine: [
          ...COMP_STATS.map(([k, word]) => (toNum(stats[k]) === null ? null : `${fixed(stats[k], 1)} ${word}`)),
          ts === null ? null : `${ts.toFixed(1)}% TS`,
        ]
          .filter(Boolean)
          .join(" · "),
        outcome: level ? `→ ${[level, programs.join(", ")].filter(Boolean).join(" · ")}` : null,
      };
    });
  const mine = text(c.band?.band);
  const bands = list(c.by_c_ram)
    .filter((b) => text(b.band))
    .map((b) => {
      const name = text(b.band);
      const rate = toNum(b.rate);
      const size = whole(b.n);
      const own = Boolean(mine) && name === mine;
      return {
        key: name,
        label: own ? `${name} · ${voice.own}` : name,
        own,
        width: rate === null ? 0 : Math.max(0, Math.min(100, rate)),
        rate: rate === null ? EMPTY : `${pyRound(rate)}%`,
        n: size === null ? EMPTY : size.toLocaleString("en-US"),
        ariaLabel: `${name}: ${rate === null ? "no rate" : `${pyRound(rate)}%`} of ${size === null ? "an unknown number of" : size} players reached D1 or pro${own ? `, ${voice.self ? "your" : voice.whoseLower} band` : ""}`,
      };
    });
  return {
    lede,
    comps,
    bands,
    bandHeading: `Reached D1 or pro, by ${division ? `${division} ` : ""}impact band`,
    insights: insightsOf(c.insights),
  };
};

// ------------------------------------------------------ peers, teammates
// The profile's own comparison (the about payload's peers and teammates,
// Kobi's 10/2 work) in a card of its own (B3 g). null without either.
export const peersView = (model, voice) => {
  const cs = model?.currentSeason;
  const comparison = cs?.comparison && (cs.comparison.tiles?.length || cs.comparison.caption) ? cs.comparison : null;
  const teammates = cs?.teammates && Array.isArray(cs.teammates.rows) && cs.teammates.rows.length ? cs.teammates : null;
  if (!comparison && !teammates) return null;
  const against = [teammates && (voice.self ? "your teammates" : "teammates"), comparison && "the peer group"].filter(Boolean).join(" and ");
  const seasons = [...new Set([teammates?.season, comparison?.season].map(text).filter(Boolean))];
  let lede;
  if (seasons.length === 1) lede = `${voice.whose} ${seasons[0]} season against ${against}`;
  else if (seasons.length > 1) lede = `Against ${against}: the teammates cover ${text(teammates.season)}, the peer group ${text(comparison.season)}`;
  else lede = `${voice.whose} season against ${against}`;
  return { lede, teammates, comparison, you: voice.self ? "You" : voice.first || "Athlete" };
};

// ---------------------------------------------------------------- notices
// The cards' data kept from the last good refresh (dataStatus stale), in
// the words of the box score's own notice (StatsProvenance: "Stats as of
// Oct 12, 2026. Live updates are paused."). dataStatus.reason is a code and
// never shows: "refreshing" (a saved copy served while the athlete's latest
// games are added; the league did answer) says so, any other (paused, a
// feed failure) reads as paused.
export const staleNotice = (status) => {
  if (!status || typeof status !== "object" || text(status.state).toLowerCase() !== "stale") return "";
  const day = asOfDay(status.asOf, { year: true });
  if (text(status.reason) === "refreshing") {
    return day
      ? `Game-by-game data as of ${day} while the latest games are added. They update on your next visit.`
      : "The latest games are being added to the game-by-game data. They update on your next visit.";
  }
  return day ? `Game-by-game data as of ${day}. Live updates are paused.` : "Live updates to the game-by-game data are paused.";
};

// What the section says above its cards: data kept from the last good
// refresh, and games left out of a partial payload.
export const dataNotices = (envelope) => {
  const out = [];
  const status = envelope?.dataStatus && typeof envelope.dataStatus === "object" ? envelope.dataStatus : {};
  out.push(staleNotice(status));
  const payload = envelope?.payload || {};
  if (payload.partial === true || status.partial === true) {
    const dropped = whole(payload.games_dropped);
    if (payload.partial_reason === "games_unreadable") out.push("The game-by-game lines could not be read for this season, so the season totals stand alone.");
    else if (dropped) out.push(`${count(dropped, "game")} without a final score ${dropped === 1 ? "is" : "are"} left out.`);
    else out.push("Games without a final score are left out.");
  }
  return out.filter(Boolean);
};

// Why the section falls back to the profile's own cards; null when there
// is nothing to explain (the athlete has no league record, for one). The
// words and the Try again (RETRY_REASONS) of the coach team page's
// unavailable state (teamPerformanceModel.REASON_COPY).
export const reasonNote = (reason, voice) => {
  if (reason === "feed_error") return "Game-by-game analytics could not load right now. Try again in a few minutes.";
  if (reason === "building") {
    return voice?.self
      ? "Your game-by-game analytics are still being prepared. Try again in a minute."
      : "Game-by-game analytics are still being prepared. Try again in a minute.";
  }
  return null;
};

// The reasons a later request may clear (the section offers Try again).
export const RETRY_REASONS = new Set(["feed_error", "building"]);

export const loadingLabel = (status, voice) => {
  if (status === "building") return voice?.self ? "Building your game-by-game analytics..." : "Building game-by-game analytics...";
  return "Loading game-by-game analytics...";
};

// A season picked in Game by Game that had nothing to show.
export const seasonNotice = (notice, options = []) => {
  if (!notice) return null;
  const option = options.find((o) => o.key === notice.key);
  const name = option ? option.label : "That season";
  if (notice.reason === "feed_error" || notice.reason === "request_failed") return `${name} could not load right now. Try again in a minute.`;
  if (notice.reason === "building") return `${name} is still being prepared. Try again in a minute.`;
  return `No game-by-game data for ${name} yet.`;
};

// ------------------------------------------------------------------ all
// Every card's view model. `envelope` is the default season's answer: the
// cards stay on it. `gamesEnvelope` is the season picked in Game by Game
// (default: the same answer); only Game by Game follows it, as the mock's
// season selector swaps that card alone (a camp or an old season never
// re-ranks the archetype or the skills above it). A card the payload has
// nothing for is null (it hides), except Game by Game, which says so.
//
// The Player Archetype's place (spec D4 c, D8) holds one of:
//   archetype       the card, when the payload names an archetype;
//   archetypeShell  the card's shell while a peer group that will rank the
//                   cards is being built (the default season's, or the
//                   rank season's): the section asks again until it is ready;
//   archetypeNote   why the archetype and the comps are left out, for a
//                   season of games the team entered;
//   radarNote       otherwise the profile's radar, with why no archetype
//                   can come (RADAR_FALLBACK_NOTE; RADAR_NO_METRICS_NOTE or
//                   radarNoMetricsNote when a group ranks the season but its
//                   box scores lack the five metrics).
// When the default season can never be ranked, the archetype, Impact,
// Skill Profile and Scoring Mix come from the latest season a peer group
// ranks (`basis`, the payload's rank_season, D4 d), and `peer` is the group
// behind those ranks; `seasonPeer` is the default season's own.
export const buildPerformanceView = ({ envelope, gamesEnvelope, model, selfView = false } = {}) => {
  const voice = voiceFor({ selfView, firstName: model?.firstName });
  const payload = envelope?.payload && typeof envelope.payload === "object" ? envelope.payload : {};
  const seasonPeer = peerOf(envelope);
  const rank = rankSeasonOf(envelope);
  const rankPeer = rank ? rankPeerOf(rank, seasonPeer) : null;
  // The payload's ranked sections come from the rank season once its group ranks.
  const basis = rankPeer?.ranked ? rank : null;
  const peer = basis ? rankPeer : seasonPeer;
  const shown = gamesEnvelope && typeof gamesEnvelope === "object" ? gamesEnvelope : envelope;
  const notices = dataNotices(envelope);
  const games = gameLogView(shown, voice, model);
  // The picked season's own notices (stale, games left out) sit in its card.
  games.notices = shown === envelope ? [] : dataNotices(shown).filter((note) => !notices.includes(note));
  const archetype = archetypeView(payload, voice, peer, basis);
  // The group being built: the rank season's, else the default season's.
  // The shell shows the payload's metrics only when that group will rank
  // them: the rank season's metrics are not the payload's yet, and a camp's
  // never are (no group of its own is ever built, so one being built ranks
  // another season), so those shells name the group only (a camp's, none).
  let waiting = null;
  let shellPayload = payload;
  if (!archetype && rankPeer?.building && !rankPeer.ranked) {
    waiting = rankPeer;
    shellPayload = {};
  } else if (!archetype && seasonPeer.building && !seasonPeer.entered) {
    waiting = seasonPeer.camp ? { ...seasonPeer, camp: false, label: "" } : seasonPeer;
    if (seasonPeer.camp) shellPayload = {};
  }
  const entered = !archetype && !waiting && seasonPeer.entered;
  // The radar's caption: a peer group ranks the cards (the rank season's,
  // or the season's own) but no archetype came, so that season's box
  // scores lack the five metrics; else no group can come for the season.
  let radarNote = null;
  if (!archetype && !waiting && !entered) {
    if (basis) radarNote = radarNoMetricsNote(basis.label);
    else radarNote = seasonPeer.ranked && !rank ? RADAR_NO_METRICS_NOTE : RADAR_FALLBACK_NOTE;
  }
  const view = {
    voice,
    peer,
    seasonPeer,
    basis,
    archetype,
    archetypeShell: waiting ? archetypeShellView(shellPayload, voice, waiting) : null,
    archetypeNote: entered ? ENTERED_ARCHETYPE_NOTE : null,
    radarNote,
    skills: skillsView(payload, voice, peer),
    scoring: scoringView(payload, voice, peer, basis),
    games,
    consistency: consistencyView(payload, voice),
    comps: compsView(payload, voice),
    peers: peersView(model, voice),
    notices,
  };
  // Every read the default season's payload gives; without any, the box
  // score keeps the profile's own season summary (the code-built fallback
  // read), whichever season Game by Game shows.
  view.hasReads = [
    view.archetype?.insights,
    view.skills?.insights,
    view.scoring?.insights,
    insightsOf(payload.game_log?.insights),
    view.consistency?.insights,
    view.comps?.insights,
  ].some((items) => Array.isArray(items) && items.length > 0);
  return view;
};

// ------------------------------------------------------------ attribution
// Whether the Performance tab ends with the data attribution line
// (common/PerformanceAttribution, Kobi 10/21), which must never sit under
// numbers the league feed did not provide:
//   - the profile's stored stats are the league feed's (the about payload's
//     statsProvider, profileModel.LEAGUE_STATS_PROVIDER): the box score and
//     the profile's own cards show its data, whichever section is drawn and
//     whether or not the new section has answered;
//   - else the new section's answer is in (performanceV2, status ready) for
//     a league season: the default season the cards read, or the season
//     Game by Game shows. A season of games the team entered (D8) is not
//     the feed's.
// So no line for stats a partner team entered (the game sheet) or its
// workbook, SportsRadar-only stats, football or a profile without stats,
// nor, for those, while the new section is loading or unavailable.
// `perf` is useAthletePerformance().
export const performanceAttributed = ({ model, perf } = {}) => {
  if (model?.statsProvider === LEAGUE_STATS_PROVIDER) return true;
  if (!hasPerformanceV2(model) || perf?.status !== "ready") return false;
  return [perf.baseEnvelope, perf.envelope].some((envelope) => envelope?.available === true && !enteredOf(envelope));
};
